/**
 * Textarea — multi-line text field with label, hint, and error states.
 */

import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, className, wrapperClassName, id, ...props }, ref) => {
    const textareaId = id || props.name;

    return (
      <div className={cn('flex flex-col gap-2', wrapperClassName)}>
        {label && (
          <label htmlFor={textareaId} className="text-sm font-medium text-foreground tracking-tight">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            'flex min-h-[96px] w-full rounded-md border border-input bg-surface px-3 py-2.5 text-sm',
            'transition-all duration-fast ease-standard',
            'placeholder:text-muted-foreground placeholder:font-normal',
            'hover:border-neutral-400',
            'focus-visible:outline-none focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600/15',
            'disabled:cursor-not-allowed disabled:opacity-40 disabled:bg-surface-secondary',
            'resize-y',
            error && 'border-error-400 focus-visible:border-error-500 focus-visible:ring-error-500/15',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${textareaId}-error` : hint ? `${textareaId}-hint` : undefined}
          {...props}
        />
        {error ? (
          <p id={`${textareaId}-error`} className="text-xs text-error-600">
            {error}
          </p>
        ) : hint ? (
          <p id={`${textareaId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
